import React, { useState, useEffect, useContext } from "react";
import styles from "../../Dashboard.module.css";
import { Modal, ModalBody } from "reactstrap";
import helper from "../../../../../utils/helper";
import { DashboardOutorgaContext } from "../../../../../contexts/DashboardOutorga";
import ReactLoading from "react-loading";
import DatePicker, { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import "react-datepicker/dist/react-datepicker.css";
import CalendarDetailsPanel from "./CalendarDetailsPanel";
import CalendarGrid from "./CalendarGrid";

const Calendar = ({ showCalendar, grantId, closeCalendar }) => {
    registerLocale("pt-BR", ptBR);
    const { calendar, isLoadingCalendar, getGrantDashboardCalendar, clearCalendar } = useContext(DashboardOutorgaContext);

    const [selectedDay, setSelectedDay] = useState(null);
    const [date, setDate] = useState(new Date());

    useEffect(() => {
        if (showCalendar) {
            setSelectedDay(null);

            if (grantId) {
                const payload = {
                    mes: date.getMonth() + 1,
                    ano: date.getFullYear()
                };
                getGrantDashboardCalendar(grantId, payload);
            }
        }
    }, [showCalendar, grantId, getGrantDashboardCalendar]);

    const generateCalendarData = (calendar) => {
        if (!calendar) return null;

        const registros = calendar.dias
            .map((diaObj) => ({
                status: diaObj.status,
                dia: diaObj.dia,
                periodoDescricao: diaObj.periodo_descricao,
                violacoes: diaObj.condicoes.map((cond) => {
                    const registrado = cond.valor_reporte ?? 0;

                    return {
                        descricao: cond.nome,
                        excedidoInicio: cond.condicao?.excedido?.inicio,
                        excedidoFim: cond.condicao?.excedido?.fim,
                        unidade: "",
                        registrado,
                        excedente: cond.quantidade_exedido,
                        percentual: cond.porcentagem ?? 0,
                    };
                }),
            }));

        return {
            sensor: {
                id: calendar.id,
                nome: calendar.nome,
            },
            referencia: {
                mes: calendar.mes,
                ano: calendar.ano,
            },
            resumo: {
                total_dias_com_violacao: calendar.dias_violacoes ?? 0,
            },
            today: new Date().getDate(),
            registros,
        };
    };

    const getDayRegister = (calendarData, dia) => {
        return calendarData.registros.find((registro) => registro.dia === dia);
    };

    const handleSelectDay = (dia) => {
        setSelectedDay(dia);
    };

    const handleCloseCalendar = () => {
        setSelectedDay(null);
        setDate(new Date());
        clearCalendar();
        closeCalendar();
    };

    const MonthTitle = React.forwardRef(({ value, onClick }, ref) => (
        <button
            title="Selecione o mês e o ano"
            className={styles.calendarMonthButton}
            onClick={onClick}
            ref={ref}
            type="button"
        >
            {helper.toCapitalizeCase(value)} <i className="fa fa-caret-down" />
        </button>
    ));

    const handleChangeDate = (date) => {
        setDate(date);
        setSelectedDay(null);

        const mes = date.getMonth() + 1;
        const ano = date.getFullYear();

        const payload = {
            mes,
            ano,
        }

        getGrantDashboardCalendar(grantId, payload);
    };

    const generateCalendar = (calendarData) => {
        const { mes, ano } = calendarData.referencia;

        const primeiroDiaSemana = new Date(ano, mes - 1, 1).getDay();
        const totalDiasMes = new Date(ano, mes, 0).getDate();

        const dias = [
            ...Array(primeiroDiaSemana).fill(null),
            ...Array.from({ length: totalDiasMes }, (_, i) => i + 1),
        ];

        const selectedRegistro = getDayRegister(calendarData, selectedDay);

        return (
            <div className={styles.calendarContainer}>
                <div className={styles.calendarHeader}>
                    <div className={styles.calendarTitleArea}>
                        <h4>
                            <DatePicker
                                selected={date}
                                onChange={handleChangeDate}
                                dateFormat="MMMM 'de' yyyy"
                                showMonthYearPicker
                                locale="pt-BR"
                                maxDate={new Date()}
                                customInput={<MonthTitle />}
                                popperClassName={styles.calendarTitleAreaDatePicker}
                            />
                        </h4>
                        <span style={{ fontSize: "0.875rem" }}>
                            {calendarData.sensor.nome} — Calendário de Outorga
                        </span>
                    </div>

                    <div
                        className={`
                            ${styles.calendarViolationBadge}
                            ${calendarData.resumo.total_dias_com_violacao ? styles.calendarViolationBadge_hasViolation : ""}
                        `}
                    >
                        <span /> {calendarData.resumo.total_dias_com_violacao} dias com violação
                    </div>
                </div>

                <div className={styles.calendarContent}>
                    <div className={styles.calendarGrid}>
                        <CalendarGrid
                            selectedDay={selectedDay}
                            dias={dias}
                            getDayRegister={getDayRegister}
                            calendarData={calendarData}
                            handleSelectDay={handleSelectDay}
                            ano={ano}
                            mes={mes}
                        />
                    </div>

                    <div className={styles.calendarDetailsPanel}>
                        <CalendarDetailsPanel
                            selectedDay={selectedDay}
                            selectedRegistro={selectedRegistro}
                        />
                    </div>
                </div>

                <div className={styles.calendarLegend}>
                    <span>
                        <span className={styles.calendarLegendRed} /> Limite excedido
                    </span>
                    <span>
                        <span className={styles.calendarLegendGray} /> Dentro do limite
                    </span>
                    <span>
                        <span className={styles.calendarLegendBlue} /> Hoje
                    </span>
                </div>
            </div>
        );
    };

    const calendarData = calendar ? generateCalendarData(calendar) : null;

    return (
        <Modal isOpen={showCalendar} toggle={handleCloseCalendar} size="xl" className={styles.calendarModal}>
            <ModalBody className={styles.calendarModalBody}>
                {isLoadingCalendar && (
                    <div className={styles.loadingCalendar}>
                        <ReactLoading
                            type="spin"
                            color="var(--background-menu)"
                            width={50}
                        />
                    </div>
                )}

                {!isLoadingCalendar && calendarData && generateCalendar(calendarData)}

                {!isLoadingCalendar && !calendarData && (
                    <div className={styles.loadingCalendar}>
                        Nenhum calendário disponível
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
}

export default Calendar;